/** /notification_rulesets/ — cross-tenant ruleset management (grid + editor).
 *
 * Hand-authored wrapper (console convention: feature modules never depend on
 * generated types). The read-only picker lookup lives in
 * `notificationLookups.listRulesets` (same endpoint, brief rows) — the block
 * editor keeps using that; these calls back the Rulesets pages and the rules
 * editor.
 *
 * Rule rows are the nfchange snapshot shape (`ConsoleNotificationRuleRow`):
 * writes are the COMPLETE ordered list, reconciled by id — rows with `id` are
 * updated, `id: null` rows are created, DB rows missing from the list are
 * DELETED. `nested_ruleset` reads as a bare id (names ride in
 * `refs.rulesets`) and writes as `{id}` / null.
 */
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

// ---------------------------------------------------------------------------
// Rule rows (shared with the notification editor)
// ---------------------------------------------------------------------------

/** Rule kinds (`Notification_Rule.TYPES`). */
export const RULE_TYPE_TPL = 0;
export const RULE_TYPE_EXPR = 1;

/** One rule row as read (snapshot shape). */
export interface RuleWire {
  id: number;
  type: number;
  tpl: number | null;
  args: Record<string, unknown> | null;
  field: string | null;
  negate: boolean;
  op: number;
  value: string | null;
  nested_ruleset: number | null;
}

/** One rule row of a write body. `id: null` = CREATE. */
export interface RuleWriteRow {
  id: number | null;
  type: number;
  tpl: number | null;
  args: Record<string, unknown> | null;
  field: string | null;
  negate: boolean;
  op: number;
  value: string | null;
  nested_ruleset: { id: number } | null;
}

/** Brief ruleset reference (refs.rulesets map values; superset of the block
 *  editor's RulesetRef). */
export interface RulesetBrief {
  id: number;
  codename: string | null;
  name: string;
  tenant: TenantRef | null;
  match_type?: number;
}

// ---------------------------------------------------------------------------
// Ruleset resource
// ---------------------------------------------------------------------------

export const MATCH_ALL = 0;
export const MATCH_ANY = 1;
export const MATCH_TYPE_LABELS: Record<number, string> = {
  [MATCH_ALL]: 'Match all rules',
  [MATCH_ANY]: 'Match any rule',
};

export interface RulesetListItem {
  id: number;
  codename: string | null;
  name: string;
  tenant: TenantRef | null;
  match_type: number;
  rules_count: number;
  created_ts: string | null;
  updated_ts: string | null;
}

export interface RulesetUsedByCounts {
  rules_in_notifications: number;
  rules_in_rulesets: number;
  notification_strings: number;
  campaigns: number;
}

export interface RulesetDetail {
  id: number;
  tenant: TenantRef | null;
  name: string;
  codename: string | null;
  match_type: number;
  created_ts: string | null;
  updated_ts: string | null;
  rules: RuleWire[];
  refs: { rulesets: Record<string, RulesetBrief> };
  used_by: RulesetUsedByCounts;
}

/** Sparse PATCH body. `rules`, when present, is the COMPLETE list. */
export interface RulesetWrite {
  name?: string;
  codename?: string | null;
  match_type?: number;
  rules?: RuleWriteRow[];
  expected_updated_ts?: string;
}

export interface RulesetCreateInput {
  tour_operator: number | 'system' | null;
  name: string;
  codename?: string | null;
  match_type?: number;
  rules?: RuleWriteRow[];
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1) — those tenants' rows plus the system rows — or 'system' alone (system rows only). */
  tour_operator_id?: TenantIdsParam;
}

function toQuery(p: ListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.pageSize) usp.set('page_size', String(p.pageSize));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.search) usp.set('search', p.search);
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(p: ListParams = {}): Promise<PaginatedResponse<RulesetListItem>> {
  return apiGet(`/notification_rulesets/${toQuery(p)}`);
}

export function retrieve(id: number): Promise<RulesetDetail> {
  return apiGet(`/notification_rulesets/${id}/`);
}

export function create(body: RulesetCreateInput): Promise<RulesetDetail> {
  return apiPost('/notification_rulesets/', body);
}

export function update(id: number, body: RulesetWrite): Promise<RulesetDetail> {
  return apiPatch(`/notification_rulesets/${id}/`, body);
}

/** 409 (`code: ruleset_in_use`, `used_by` counts) while anything references the
 *  ruleset — surface the counts, don't retry. */
export function remove(id: number): Promise<void> {
  return apiDelete(`/notification_rulesets/${id}/`);
}

// ---------------------------------------------------------------------------
// Rule-args schema (client-side rule validation)
// ---------------------------------------------------------------------------

/** The notification-rule args schema (`ConsoleRuleArgsSchema`, static per
 *  deploy). Interpreted by `features/notifications/rules/ruleSchema.ts`, which
 *  owns its type — returned untyped here so this module stays feature-free. */
export function ruleSchema(): Promise<unknown> {
  return apiGet<unknown>('/notification_rulesets/rule_schema/');
}
